// The text zones a "vierge" (blank) cover prints.
//
// ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
//
// 2026-08-20. A customer asked for his own photo on the front cover. It worked:
// the photo was copied, `cover_id` was set to null — which IS the vierge cover,
// the blank one a photo fills — and the album re-rendered. And his title
// disappeared.
//
// ⚠️ `cover_id: null` REMOVES THE TEXT ZONES ALONG WITH THE ARTWORK. The PDF
// draws the title from `input.cover.text_zones`, and `buildAlbumPdfInput` only
// loads a cover row when there IS a cover id. So a blank cover had no zones,
// and `cover_destination_text` — the words the customer had just asked us to
// change to « my trip » — were simply not drawn. Owner: "he add it over the
// texts. i want to keep the texts and the image below."
//
// The cover PICKER already defines exactly these zones for its '__blank__'
// entry, inline in a JSX component where no renderer could reach them. Moving
// them here is what makes the printed book match what the picker promised.
//
// ⚠️ THE PDF DRAWS TEXT AFTER THE IMAGE. `generateCoverPdf` renders a
// blank-cover photo cover-fit and then draws the zones on top — so this is the
// only thing that was missing; no draw-order change was needed.

export interface BlankCoverZone {
  type: 'destination' | 'year';
  x: number; y: number; width: number; height: number;
  font_size: number;
  font_family: string;
  color: string;
  align: 'center';
  default_text: string;
}

/**
 * The zones a blank cover prints.
 *
 * @param onPhoto true when the customer's own image fills the front panel.
 *
 * ⚠️ A PHOTO COVER IS A DIFFERENT DESIGN PROBLEM, so it gets different zones —
 * and the reason is not taste, it is that both defaults were chosen for a
 * situation that no longer applies:
 *
 *   SIZE. The picker's 64 is sized for a bare white board, where modest type in
 *   the upper third reads fine. Over a full-bleed photograph the same 64 is a
 *   caption, not a title — « my trip » came out smaller than the surfboard's
 *   brand logo. The cover this customer had before used 410 at the same 1860
 *   reference, which is what a title on this book actually looks like.
 *
 *   POSITION. Upper-third (y 0.3) puts the words in the middle of the picture.
 *   The top band (y 0.06) is where the title sat on every catalogue cover, and
 *   it is the part of a photo most likely to be sky or open space.
 *
 *   INK. The picker's dark ink is chosen for a white board; on a photograph it
 *   is a coin flip, and this cover was white type over a bright sea.
 *
 * ⚠️ THE BARE-BOARD VALUES STAY EXACTLY AS THE PICKER DEFINES THEM. Changing
 * those would restyle the vierge cover for every customer who picked it on a
 * white board — a redesign nobody asked for, off the back of one photo cover.
 */
export function blankCoverZones(onPhoto: boolean): BlankCoverZone[] {
  if (onPhoto) {
    return [
      {
        type: 'destination',
        // The catalogue covers' title band: full width, top of the panel.
        x: 0.1, y: 0.06, width: 0.83, height: 0.19,
        font_size: 410,
        font_family: 'Londrina Solid',
        color: '#FFFFFF',
        align: 'center',
        default_text: 'DESTINATION',
      },
      {
        type: 'year',
        x: 0.1, y: 0.26, width: 0.83, height: 0.09,
        font_size: 150,
        font_family: 'Londrina Solid',
        color: '#F0EDE6',
        align: 'center',
        default_text: '2025',
      },
    ];
  }
  // The picker's '__blank__' zones, unchanged — see the warning above.
  return [
    {
      type: 'destination',
      x: 0.1, y: 0.3, width: 0.8, height: 0.12,
      font_size: 64,
      font_family: 'Londrina Solid',
      color: '#1A1A1A',
      align: 'center',
      default_text: 'DESTINATION',
    },
    {
      type: 'year',
      x: 0.1, y: 0.45, width: 0.8, height: 0.08,
      font_size: 32,
      font_family: 'Londrina Solid',
      color: '#8A8578',
      align: 'center',
      default_text: '2025',
    },
  ];
}
